import { cn } from "../foundations/cn.js";
import { Amount } from "./Amount.js";
import { formatChance } from "./odds.js";
import { RarityIcon } from "./RarityMarker.js";
import { RARITY_FILL, RARITY_LABELS, type RarityTier } from "./rarity.js";

/** One tier's row: what its items are worth and its published chance. */
export interface OddsTableRow {
  tier: RarityTier;
  /** The tier's name in tenant copy; read with the row, shown when `showLabels`. */
  label?: string;
  /** The least and most valuable item in the tier, in cents; left out when none is listed. */
  min?: number;
  max?: number;
  /** The tier's published chance in percent. */
  chance: number;
}

export interface OddsTableProps {
  rows: readonly OddsTableRow[];
  /** The table's name for screen readers: "Odds by tier". */
  caption?: string;
  /** Shows each tier's word beside its shape. */
  showLabels?: boolean;
  /** Column names for screen readers. */
  headers?: { tier: string; value: string; chance: string };
  className?: string;
}

/**
 * A box's published odds by tier as rows, rarest first: the tier's bar and shape, what its items are
 * worth (from and to) and its chance. The fuller sibling of OddsBar. Never animated.
 */
export function OddsTable({
  rows,
  caption = "Odds by tier",
  showLabels = false,
  headers = { tier: "Tier", value: "Value", chance: "Chance" },
  className,
}: OddsTableProps) {
  const shown = rows.filter((row) => row.chance > 0);
  return (
    <table className={cn("w-full border-separate border-spacing-y-2 font-body", className)}>
      <caption className="sr-only">{caption}</caption>
      <thead className="sr-only">
        <tr>
          <th scope="col">{headers.tier}</th>
          <th scope="col">{headers.value}</th>
          <th scope="col">{headers.chance}</th>
        </tr>
      </thead>
      <tbody>
        {shown.map((row) => {
          const word = row.label ?? RARITY_LABELS[row.tier];
          return (
            <tr key={row.tier}>
              <th
                scope="row"
                className="relative w-0 rounded-s-xl border-y border-s border-border-subtle bg-bg-surface py-3.5 ps-5 pe-2 text-start align-middle font-normal"
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "absolute inset-y-3 start-0 w-1 rounded-e-pill",
                    RARITY_FILL[row.tier],
                  )}
                />
                {/* Each cell's content is a block of one fixed height, so the shape, the word and the
                    numbers center on the same line whatever their fonts' metrics. */}
                <span className="flex h-6 items-center gap-2 whitespace-nowrap text-body-b2-strong text-text-primary">
                  <RarityIcon tier={row.tier} size="lg" />
                  <span className={showLabels ? undefined : "sr-only"}>{word}</span>
                </span>
              </th>
              <td className="border-y border-border-subtle bg-bg-surface px-2 py-3.5 align-middle">
                <span className="flex h-6 flex-wrap items-center gap-x-1.5 text-text-primary">
                  {row.min !== undefined && <Amount cents={row.min} size="meta" trimZeroCents />}
                  {row.max !== undefined && row.min !== undefined && row.max !== row.min && (
                    <>
                      <span aria-hidden="true" className="text-text-tertiary">
                        –
                      </span>
                      <span className="sr-only">to</span>
                      <Amount cents={row.max} size="meta" trimZeroCents />
                    </>
                  )}
                </span>
              </td>
              <td className="rounded-e-xl border-y border-e border-border-subtle bg-bg-surface py-3.5 ps-2 pe-3.5 align-middle">
                <span className="flex h-6 items-center justify-end font-mono text-mono-price text-text-primary tabular-nums">
                  {formatChance(row.chance)}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
